import { v4 as uuidv4 } from "uuid"

const UUID_SURVEY = uuidv4()
const URL_API = `https://api.peoplevine.com/survey.asmx`

type AuthType = {
  api_username: string
  api_password: string
  api_key: string
  company_no: number
  username: string
  password: string
}

type ScalarValue = string | number | boolean

type SurveyPayload = {
  survey_no: number
  survey_field_no: number
  survey_answer: ScalarValue
  response_id: string
  survey_group_no: number
}[]

type PayloadAnswerSurveyType = {
  answers: SurveyPayload
  auth: AuthType
}

type SurveyApplicationPayloadType = {
  survey_group_no: number;
  response_id: string;
  customer_no: number;
  company_no: number;
  application_status: string;
}

type PayloadCreateSurveyApplicationType = {
  auth: AuthType,
  fields: SurveyApplicationPayloadType
}

interface OnSubmitProps {
  surveyNoId: number
  surveyGroupId: number
  payload: Record<number, string>
  surveyAuth?: AuthType
  options?: RequestInit
}

const useSurveyApplication = () => {

  const sendAnswerSurvey = async (payloadToSend: PayloadAnswerSurveyType, options?: RequestInit): Promise<number | void> => {
    const { answers, auth } = payloadToSend;

    if (!answers.length) {
      console.warn("No answers provided. Aborting request.");
      return;
    }

    const request: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payloadToSend),
      ...options,
    };

    if (process.env.NODE_ENV === "development") {
      console.debug("Development Mode: Sending survey payload", {
        requestBody: request.body,
        auth,
      });
      return;
    }

    try {
      const response = await fetch(`${URL_API}/answerSurvey`, request);

      if (!response.ok) {
        console.error(`Failed to send survey. Status: ${response.status}`);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const body = await response.json();
      const content = JSON.parse(body?.d || "{}");
      const customerNo = content?.returnObject?.customer?.customer_no;

      if (!customerNo) {
        console.warn("Customer number not found in response.");
      }

      return customerNo;
    } catch (error) {
      console.error("Error occurred while sending survey:", error);
    }
  };

  const createSurveyApplication = async (payloadToSend: PayloadCreateSurveyApplicationType, options?: RequestInit): Promise<void> => {

    const request: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payloadToSend),
      ...options,
    }; 

    try {
      await fetch(`${URL_API}/createSurveyApplication`, request)
    } catch (error) {
      console.error("Error occurred while sending survey:", error);
    }

  }

  const onSubmit = async (
    { surveyNoId, surveyGroupId, payload, surveyAuth, options }: OnSubmitProps
  ): Promise<void> => {
    const answers: SurveyPayload = []

    Object.entries(payload).forEach(([key, value]) =>
      answers.push({
        survey_no: surveyNoId,
        survey_field_no: Number.parseInt(key),
        survey_answer: value,
        response_id: UUID_SURVEY,
        survey_group_no: surveyGroupId,
      })
    )

    const auth = surveyAuth || {
      api_username: import.meta.env.REACT_APP_PUBLIC_API_USERNAME || "",
      api_password: import.meta.env.REACT_APP_PUBLIC_API_PASSWORD || "",
      api_key: import.meta.env.REACT_APP_PUBLIC_API_KEY || "",
      company_no: Number(import.meta.env.REACT_APP_PUBLIC_COMPANY_NO || 0),
      username: import.meta.env.REACT_APP_PUBLIC_USERNAME || "",
      password: import.meta.env.REACT_APP_PUBLIC_PASSWORD || "",
    }

    const payloadToSend = { answers, auth }

    const customerNo = await sendAnswerSurvey(payloadToSend, options)

    const payloadToSendCreateApplication: PayloadCreateSurveyApplicationType = {
      auth,
      fields: {
        survey_group_no: surveyGroupId,
        response_id: UUID_SURVEY,
        customer_no: customerNo || 0,
        company_no: surveyAuth?.company_no || 0,
        application_status: 'pending',
      },
    }

    await createSurveyApplication(payloadToSendCreateApplication)
    location.href = "/thank-you"
  }

  return {
    onSubmit,
  }
}

export default useSurveyApplication
